import { useCallback, useEffect, useState } from "react";
import { InventoryAPI } from "@/apis/material";
import { Number } from "@/components/Numeric";
import { Date } from "@/components/Date";
import { formatDate } from "@/utils";
import { Button, Input, Modal, Table, Tooltip } from "@arco-design/web-react";
import { IconZoomIn } from "@arco-design/web-react/icon";
import _ from "lodash";

const formatLotText = (inv) => {
    if (!inv) return undefined;
    const parts = [];
    const location = [inv.warehouseName, inv.locationName].filter(Boolean).join('-');
    if (location) parts.push(location);
    if (inv.lot) parts.push(inv.lot);
    if (inv.expiredDate) parts.push(formatDate(inv.expiredDate));
    return parts.length > 0 ? parts.join(' | ') : undefined;
};

const InventorySelect = ({ warehouseId, getItemId, getPickQty, getQtyFulfilled, onChange, onSelect, value, readOnly }) => {
    const [data, setData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [displayValue, setDisplayValue] = useState();

    const getInventoryLot = useCallback(async () => {
        if (value) {
            const itemId = getItemId();
            if (itemId) {
                try {
                    const inventories = await InventoryAPI.itemInventories([itemId]);
                    const key = typeof value === 'object' ? value : null;
                    if (key) {
                        const inv = inventories.find(i =>
                            i.warehouseId === key.warehouseId
                            && (i.locationId === key.locationId || (!i.locationId && !key.locationId))
                            && (i.lot === key.lot || (!i.lot && !key.lot)));
                        if (inv) {
                            setDisplayValue(formatLotText(inv));
                        }
                    }
                } catch (e) {
                    // ignore
                }
            }
        } else {
            setDisplayValue(undefined);
        }
    }, [value, getItemId]);

    const getLots = useCallback(async () => {
        const itemId = getItemId();
        if (itemId) {
            try {
                let inventories;
                if (warehouseId) {
                    inventories = await InventoryAPI.itemInventoryByWarehouse(itemId, warehouseId);
                } else {
                    inventories = await InventoryAPI.itemInventoryAvailable(itemId);
                }
                setData(_.orderBy((inventories || []).filter(i => i.availableQty > 0), ['expiredDate'], ['asc']));
            } catch (e) {
                setData([]);
            }
        }
    }, [warehouseId, getItemId]);

    useEffect(() => {
        getInventoryLot();
    }, [getInventoryLot]);

    const getRequiredQty = () => {
        return getPickQty ? getPickQty() : (getQtyFulfilled ? getQtyFulfilled() : 0);
    };

    return (
        <>
            <Input.Search
                readOnly
                searchButton={!readOnly}
                value={displayValue}
                onSearch={() => {
                    if (readOnly) return;
                    setVisible(true);
                }}
            />
            <Modal
                visible={visible}
                style={{ width: 800 }}
                afterOpen={getLots}
                closeIcon={false}
                footer={null}
                onCancel={() => setVisible(false)}
            >
                <Table
                    rowKey="id"
                    columns={[
                        { title: "Item Name", dataIndex: "itemName" },
                        { title: "Warehouse", dataIndex: "warehouseName" },
                        { title: "Location", dataIndex: "locationName" },
                        {
                            title: "Qty",
                            dataIndex: "availableQty",
                            render: (col, record) => <Number value={record.availableQty} />
                        },
                        { title: "Lot#", dataIndex: "lot" },
                        { title: "Expiration Date", dataIndex: "expiredDate", render: (col, record) => record.expiredDate ? <Date value={record.expiredDate} /> : "" },
                        {
                            title: "",
                            width: 60,
                            render: (col, record) => {
                                const requiredQty = getRequiredQty();
                                const insufficient = requiredQty > 0 && record.availableQty < requiredQty;
                                return (
                                    <Tooltip
                                        content={`Available qty ${record.availableQty} is less than the required ${requiredQty}. You can still select it and split the remaining qty into another line.`}
                                        disabled={!insufficient}
                                    >
                                        <Button
                                            type="text"
                                            icon={<IconZoomIn />}
                                            onClick={() => {
                                                setVisible(false);
                                                const keyValue = {
                                                    warehouseId: record.warehouseId,
                                                    locationId: record.locationId,
                                                    lot: record.lot
                                                };
                                                onChange && onChange(keyValue);
                                                onSelect && onSelect(record);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            }
                        },
                    ]}
                    data={data}
                />
            </Modal>
        </>
    );
};

export default InventorySelect;
